/**
 * Developer Settings Utility
 *
 * Provides a simple interface to manage developer-only features
 * using localStorage for persistence across sessions.
 *
 * Usage:
 * - Enable developer mode: DeveloperSettings.setDeveloperMode(true)
 * - Disable: DeveloperSettings.setDeveloperMode(false)
 * - Check status: DeveloperSettings.isDeveloperMode()
 * - Toggle: DeveloperSettings.toggleDeveloperMode()
 */

import { logger } from './logger';

const STORAGE_KEY = 'bo360_developer_settings';

interface DeveloperSettingsConfig {
  enabled: boolean;
  skipAuth: boolean;
  showDebugInfo: boolean;
  bypassCacheWarmup: boolean;
  forceDesktopView: boolean;
}

const DEFAULT_SETTINGS: DeveloperSettingsConfig = {
  enabled: false,
  skipAuth: false,
  showDebugInfo: false,
  bypassCacheWarmup: false,
  forceDesktopView: false,
};

export class DeveloperSettings {
  /**
   * Get all developer settings from localStorage
   */
  private static getSettings(): DeveloperSettingsConfig {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return DEFAULT_SETTINGS;

      const parsed = JSON.parse(stored);
      return { ...DEFAULT_SETTINGS, ...parsed };
    } catch (error) {
      logger.error('[DeveloperSettings] Failed to load settings:', error);
      return DEFAULT_SETTINGS;
    }
  }

  /**
   * Save developer settings to localStorage
   */
  private static saveSettings(settings: DeveloperSettingsConfig): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch (error) {
      logger.error('[DeveloperSettings] Failed to save settings:', error);
    }
  }

  /**
   * Check if developer mode is enabled
   */
  static isDeveloperMode(): boolean {
    return this.getSettings().enabled;
  }

  /**
   * Enable or disable developer mode
   */
  static setDeveloperMode(enabled: boolean): void {
    const settings = this.getSettings();
    settings.enabled = enabled;

    // If disabling developer mode, reset all other developer settings
    if (!enabled) {
      settings.skipAuth = false;
      settings.showDebugInfo = false;
      settings.bypassCacheWarmup = false;
      settings.forceDesktopView = false;
    }

    this.saveSettings(settings);
  }

  /**
   * Toggle developer mode on/off
   */
  static toggleDeveloperMode(): boolean {
    const newState = !this.isDeveloperMode();
    this.setDeveloperMode(newState);
    return newState;
  }

  /**
   * Check if authentication should be skipped
   * Checks both localStorage settings AND environment variable for MCP tools
   * IMPORTANT: Has production safeguards to prevent skip-auth on production domains
   */
  static shouldSkipAuth(): boolean {
    // PRODUCTION SAFEGUARD 1: Never skip auth on production domain
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      if (hostname === 'eroland.me' || hostname.endsWith('.eroland.me')) {
        logger.warn('[DeveloperSettings.shouldSkipAuth] BLOCKED: Production domain detected');
        return false;
      }
    }

    // PRODUCTION SAFEGUARD 2: Never skip auth in production build
    if (import.meta.env.PROD) {
      return false;
    }

    // First check environment variable (fastest path for MCP tools)
    const envVar = import.meta.env?.VITE_DEV_SKIP_AUTH;
    if (envVar === 'true') {
      logger.log('[DeveloperSettings.shouldSkipAuth] Returning TRUE (env var)');
      return true;
    }
    // Then check localStorage settings
    const settings = this.getSettings();
    const result = settings.enabled && settings.skipAuth;
    return result;
  }

  /**
   * Enable/disable authentication bypass
   * Only works if developer mode is enabled
   */
  static setSkipAuth(skip: boolean): void {
    const settings = this.getSettings();
    if (!settings.enabled) {
      logger.warn('[DeveloperSettings] Cannot set skipAuth when developer mode is disabled');
      return;
    }

    settings.skipAuth = skip;
    this.saveSettings(settings);
  }

  /**
   * Check if debug info should be shown
   */
  static shouldShowDebugInfo(): boolean {
    const settings = this.getSettings();
    return settings.enabled && settings.showDebugInfo;
  }

  /**
   * Enable/disable debug info display
   */
  static setShowDebugInfo(show: boolean): void {
    const settings = this.getSettings();
    if (!settings.enabled) {
      logger.warn('[DeveloperSettings] Cannot set showDebugInfo when developer mode is disabled');
      return;
    }

    settings.showDebugInfo = show;
    this.saveSettings(settings);
  }

  /**
   * Check if cache warmup should be bypassed
   */
  static shouldBypassCacheWarmup(): boolean {
    const settings = this.getSettings();
    return settings.enabled && settings.bypassCacheWarmup;
  }

  /**
   * Enable/disable cache warmup bypass
   */
  static setBypassCacheWarmup(bypass: boolean): void {
    const settings = this.getSettings();
    if (!settings.enabled) {
      logger.warn('[DeveloperSettings] Cannot set bypassCacheWarmup when developer mode is disabled');
      return;
    }

    settings.bypassCacheWarmup = bypass;
    this.saveSettings(settings);
  }

  /**
   * Check if desktop view should be forced
   */
  static shouldForceDesktopView(): boolean {
    const settings = this.getSettings();
    return settings.enabled && settings.forceDesktopView;
  }

  /**
   * Enable/disable force desktop view
   * Only works if developer mode is enabled
   */
  static setForceDesktopView(force: boolean): void {
    const settings = this.getSettings();
    if (!settings.enabled) {
      logger.warn('[DeveloperSettings] Cannot set forceDesktopView when developer mode is disabled');
      return;
    }

    settings.forceDesktopView = force;
    this.saveSettings(settings);
  }

  /**
   * Get all settings (for UI display)
   */
  static getAllSettings(): DeveloperSettingsConfig {
    return this.getSettings();
  }

  /**
   * Reset all developer settings to default
   */
  static reset(): void {
    this.saveSettings(DEFAULT_SETTINGS);
  }

  /**
   * Quick enable for development (skip auth + bypass cache warmup)
   */
  static quickEnable(): void {
    const settings: DeveloperSettingsConfig = {
      enabled: true,
      skipAuth: true,
      showDebugInfo: false,
      bypassCacheWarmup: true,
    };
    this.saveSettings(settings);
    logger.log('[DeveloperSettings] Quick enabled (skip auth + bypass warmup)');
  }

  /**
   * Console helper - expose to window for easy toggling
   */
  static exposeToConsole(): void {
    if (typeof window !== 'undefined') {
      (window as any).devSettings = {
        enable: () => this.quickEnable(),
        disable: () => this.setDeveloperMode(false),
        toggle: () => this.toggleDeveloperMode(),
        status: () => {
          const settings = this.getAllSettings();
          console.table(settings);
          return settings;
        },
        skipAuth: (skip: boolean) => this.setSkipAuth(skip),
        forceDesktopView: (force: boolean) => this.setForceDesktopView(force),
        reset: () => this.reset(),
      };
      logger.log('  window.devSettings.enable()  - Quick enable dev mode');
      logger.log('  window.devSettings.disable() - Disable dev mode');
      logger.log('  window.devSettings.toggle()  - Toggle dev mode');
      logger.log('  window.devSettings.status()  - Show current settings');
      logger.log('  window.devSettings.skipAuth(true/false) - Toggle auth skip');
      logger.log('  window.devSettings.forceDesktopView(true/false) - Toggle desktop view');
      logger.log('  window.devSettings.reset()   - Reset to defaults');
    }
  }
}

// Auto-expose to console in development
if (import.meta.env.DEV) {
  DeveloperSettings.exposeToConsole();
}

// Auto-enable skip-auth via environment variable (for MCP tools: Playwright, Skyvern, Puppeteer)
// Set VITE_DEV_SKIP_AUTH=true in .env.development or .env.local
if (typeof import.meta !== 'undefined' && import.meta.env?.VITE_DEV_SKIP_AUTH === 'true') {
  DeveloperSettings.quickEnable();
}
